import { useEffect, useMemo, useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import { fetchCustomers, type Customer } from '@/services/customers';
import { fetchVariants, type VariantOption } from '@/services/variants';
import { createOrder } from '@/services/orders';
import { useShippingConfig } from '@/hooks/useShippingConfig';
import { computeShippingCost, COLOMBIA_CITIES } from '@/services/shipping';

interface LineItem {
  variantId: string;
  quantity: string;
  // Si está marcado, este ítem queda exento del cobro de envío.
  exemptShipping: boolean;
}

interface OrderModalProps {
  onClose: () => void;
  onSaved: () => void;
}

export default function OrderModal({ onClose, onSaved }: OrderModalProps) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [variants, setVariants] = useState<VariantOption[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<'pending_payment' | 'received' | 'payment_confirmed'>('received');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [lines, setLines] = useState<LineItem[]>([{ variantId: '', quantity: '1', exemptShipping: false }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Envío con transportadora (Interrapidísimo). Solo aplica a línea sencilla.
  const shippingConfig = useShippingConfig();
  const [requiresShipping, setRequiresShipping] = useState(false);
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [recipientIdNumber, setRecipientIdNumber] = useState('');
  const [department, setDepartment] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  // Valor de envío editado manualmente. Vacío = cálculo automático por peso.
  const [shippingOverride, setShippingOverride] = useState('');

  useEffect(() => {
    fetchCustomers().then((r) => {
      setCustomers(r.data);
      if (r.data[0]) setCustomerId(r.data[0].id);
    });
    fetchVariants().then((r) => setVariants(r.data));
  }, []);

  const total = useMemo(
    () =>
      lines.reduce((sum, l) => {
        const v = variants.find((x) => x.id === l.variantId);
        return sum + (v ? v.retailPrice * l.quantity : 0);
      }, 0),
    [lines, variants],
  );

  // Solo los productos de línea SENCILLA cobran flete. La línea premium ya
  // incluye el envío en su precio, así que el interruptor queda inhabilitado.
  const cartChargesShipping = useMemo(
    () =>
      lines.some(
        (l) =>
          !l.exemptShipping &&
          variants.find((x) => x.id === l.variantId)?.chargeShipping === true,
      ),
    [lines, variants],
  );

  // Peso a cobrar: solo los ítems de línea sencilla NO eximidos suman flete.
  const chargeableWeight = useMemo(
    () =>
      lines.reduce((sum, l) => {
        if (l.exemptShipping) return sum;
        const v = variants.find((x) => x.id === l.variantId);
        if (!v || !v.chargeShipping) return sum;
        return sum + (v.weightKg ?? 0.5) * Math.max(1, Number(l.quantity) || 1);
      }, 0),
    [lines, variants],
  );

  useEffect(() => {
    if (!cartChargesShipping && requiresShipping) {
      setRequiresShipping(false);
    }
  }, [cartChargesShipping, requiresShipping]);

  const shippingCost =
    requiresShipping && shippingOverride.trim() !== ''
      ? Number(shippingOverride) || 0
      : requiresShipping
        ? computeShippingCost(city, shippingConfig, chargeableWeight)
        : 0;
  const grandTotal = total + shippingCost;

  function toggleShipping() {
    if (!cartChargesShipping) return;
    const next = !requiresShipping;
    setRequiresShipping(next);
    if (next && !recipientName.trim()) {
      const c = customers.find((x) => x.id === customerId);
      if (c) setRecipientName(`${c.firstName ?? ''} ${c.lastName ?? ''}`.trim());
    }
  }

  function updateLine(i: number, patch: Partial<LineItem>) {
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function addLine() {
    setLines((ls) => [...ls, { variantId: '', quantity: '1', exemptShipping: false }]);
  }
  function removeLine(i: number) {
    setLines((ls) => ls.filter((_, idx) => idx !== i));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!customerId) {
      setError('Selecciona un cliente.');
      return;
    }
    const items = lines
      .filter((l) => l.variantId)
      .map((l) => {
        const v = variants.find((x) => x.id === l.variantId)!;
        return {
          variantId: l.variantId,
          quantity: Math.max(1, Number(l.quantity) || 1),
          unitPrice: v.retailPrice,
          unitCost: v.unitCost,
        };
      });
    if (items.length === 0) {
      setError('Agrega al menos un producto.');
      return;
    }

    if (requiresShipping) {
      if (
        !recipientName.trim() ||
        !recipientPhone.trim() ||
        !recipientIdNumber.trim() ||
        !department.trim() ||
        !city.trim() ||
        !address.trim()
      ) {
        setError('Completa los datos de despacho (destinatario, teléfono, cédula, departamento, ciudad y dirección).');
        return;
      }
    }

    setSaving(true);
    const shipping = requiresShipping
      ? {
          recipientName: recipientName.trim(),
          recipientPhone: recipientPhone.trim(),
          recipientIdNumber: recipientIdNumber.trim(),
          recipientEmail: recipientEmail.trim(),
          department: department.trim(),
          city: city.trim(),
          address: address.trim(),
          notes: deliveryNotes.trim(),
        }
      : undefined;
    const res = await createOrder({ customerId, notes, items, status, paymentMethod, shipping, shippingCostOverride: requiresShipping ? shippingCost : undefined });
    setSaving(false);

    if (!res.success) {
      setError(res.error || 'No se pudo crear el pedido.');
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <Modal
      title="Nuevo Pedido"
      subtitle="Registra un pedido manual para un cliente."
      onClose={onClose}
      maxWidth="max-w-xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div>
          <label className={labelCls}>Cliente *</label>
          <select className={inputCls} value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.firstName} {c.lastName}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Estado inicial</label>
            <select
              className={inputCls}
              value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}
            >
              <option value="pending_payment">Pendiente de pago</option>
              <option value="received">Recibido (contra entrega)</option>
              <option value="payment_confirmed">Pago confirmado</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Método de pago</label>
            <select
              className={inputCls}
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <option value="cash">Efectivo / Contra entrega</option>
              <option value="transfer">Transferencia bancaria</option>
              <option value="nequi_negocios">Nequi Negocios</option>
              <option value="card_credit">Tarjeta crédito</option>
              <option value="card_debit">Tarjeta débito</option>
              <option value="pse">PSE</option>
              <option value="other">Otro</option>
            </select>
          </div>
        </div>

        <div>
          <label className={labelCls}>Productos</label>
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="border border-background-200 rounded-md p-2 space-y-1.5">
                <select
                  className={`${inputCls} w-full`}
                  value={l.variantId}
                  onChange={(e) => updateLine(i, { variantId: e.target.value })}
                >
                  <option value="">Selecciona variante...</option>
                  {variants.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.productName} · {v.sizeName}
                    </option>
                  ))}
                </select>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500 whitespace-nowrap">Cantidad</span>
                  <input
                    type="number"
                    min="1"
                    className={`${inputCls} w-24`}
                    value={l.quantity}
                    onChange={(e) => updateLine(i, { quantity: e.target.value })}
                  />
                  {lines.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLine(i)}
                      className="ml-auto w-8 h-8 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                      aria-label="Quitar"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  )}
                </div>
                {variants.find((x) => x.id === l.variantId)?.chargeShipping && (
                  <label className="flex items-center gap-2 text-xs text-foreground-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={l.exemptShipping}
                      onChange={(e) => updateLine(i, { exemptShipping: e.target.checked })}
                      className="w-3.5 h-3.5 accent-accent-500 cursor-pointer"
                    />
                    Eximir / omitir costo de envío de este ítem
                  </label>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addLine}
            className="mt-2 text-xs text-accent-600 hover:text-accent-700 font-medium cursor-pointer"
          >
            <i className="ri-add-line mr-1"></i>Agregar producto
          </button>
        </div>

        <div>
          <label className={labelCls}>Notas</label>
          <textarea
            className={`${inputCls} resize-none`}
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Instrucciones o comentarios del pedido..."
          />
        </div>

        <div className={`border rounded-lg p-3.5 space-y-3 ${cartChargesShipping ? 'border-background-200' : 'border-background-200 bg-background-50'}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <i className={`ri-truck-line text-lg ${cartChargesShipping ? 'text-accent-600' : 'text-foreground-400'}`}></i>
              <div>
                <p className={`text-sm font-medium ${cartChargesShipping ? 'text-foreground-900' : 'text-foreground-500'}`}>¿Requiere Envío con Transportadora?</p>
                <p className="text-xs text-foreground-500">
                  {cartChargesShipping
                    ? 'Interrapidísimo · suma el flete al total'
                    : 'Solo disponible si el pedido incluye productos de línea sencilla (la línea premium ya incluye el envío).'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={toggleShipping}
              role="switch"
              aria-checked={requiresShipping}
              disabled={!cartChargesShipping}
              className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${cartChargesShipping ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'} ${requiresShipping ? 'bg-accent-500' : 'bg-background-300'}`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${requiresShipping ? 'translate-x-5' : ''}`}
              ></span>
            </button>
          </div>

          {requiresShipping && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <div className="sm:col-span-2">
                <label className={labelCls}>Nombre destinatario *</label>
                <input
                  type="text"
                  className={inputCls}
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="Nombre de quien recibe"
                />
              </div>
              <div>
                <label className={labelCls}>Celular *</label>
                <input
                  type="tel"
                  className={inputCls}
                  value={recipientPhone}
                  onChange={(e) => setRecipientPhone(e.target.value)}
                  placeholder="+57 300 000 0000"
                />
              </div>
              <div>
                <label className={labelCls}>Cédula / NIT *</label>
                <input
                  type="text"
                  className={inputCls}
                  value={recipientIdNumber}
                  onChange={(e) => setRecipientIdNumber(e.target.value)}
                  placeholder="Ej: 1018452100"
                />
              </div>
              <div>
                <label className={labelCls}>Correo electrónico</label>
                <input
                  type="email"
                  className={inputCls}
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="correo@ejemplo.com"
                />
              </div>
              <div>
                <label className={labelCls}>Departamento *</label>
                <input
                  type="text"
                  className={inputCls}
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  placeholder="Ej: Cundinamarca"
                />
              </div>
              <div>
                <label className={labelCls}>Ciudad / Municipio *</label>
                <input
                  type="text"
                  list="pcp-order-cities"
                  className={inputCls}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Ej: Bogotá"
                />
                <datalist id="pcp-order-cities">
                  {COLOMBIA_CITIES.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Dirección completa *</label>
                <input
                  type="text"
                  className={inputCls}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Ej: Calle 123 #45-67, Apto 302, Torre B"
                />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Observaciones de entrega (torre, apto, piso, etc.)</label>
                <textarea
                  className={`${inputCls} resize-none`}
                  rows={2}
                  maxLength={500}
                  value={deliveryNotes}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                  placeholder="Torre, apartamento, piso, etc. — Ej: Torre 2, Apto 302, Piso 3, portón negro"
                />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Costo de envío ($) — editable</label>
                <input
                  type="number"
                  min="0"
                  className={inputCls}
                  value={shippingOverride !== '' ? shippingOverride : String(computeShippingCost(city, shippingConfig, chargeableWeight))}
                  onChange={(e) => setShippingOverride(e.target.value)}
                  placeholder="Valor del flete"
                />
                <p className="text-xs text-foreground-400 mt-1">
                  Sugerido automáticamente según la ciudad de destino. Puedes ajustarlo manualmente.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="bg-accent-50 border border-accent-100 rounded-md px-4 py-3 space-y-1">
          {requiresShipping && (
            <>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-600">Productos</span>
                <span className="text-foreground-800">${total.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-600">Flete {city ? `(${city})` : ''}</span>
                <span className="text-foreground-800">
                  {city ? `$${shippingCost.toLocaleString()}` : 'Selecciona la ciudad'}
                </span>
              </div>
            </>
          )}
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-foreground-700">Total</span>
            <span className="text-lg font-bold text-foreground-950">${grandTotal.toLocaleString()}</span>
          </div>
        </div>

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...
              </>
            ) : (
              <>
                <i className="ri-save-line mr-1.5"></i>Crear pedido
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}